import { CheckIcon, CloseIcon } from '@diligentcorp/atlas-react-icons';
import { LoadingProgress } from '@issuemanager/apps/client/components';
import { useHeaderStore } from '@issuemanager/apps/client/contexts';
import {
  useAllMutations,
  useAllQueries,
  useMonitorAddIssueTemplatesFormSubmit,
  useMonitorEditIssueTemplateFormLoad,
  useMonitorEditIssueTemplateFormSubmit,
} from '@issuemanager/apps/client/hooks';
import { paths } from '@issuemanager/apps/client/paths';
import {
  WithTheme,
  useAtlasSemanticThemeTokens,
} from '@issuemanager/apps/client/theme';
import {
  useCreateIssueTemplate,
  useUpdateIssueTemplate,
} from '@issuemanager/client/features/issue-templates';
import {
  ActionStatus,
  ISSUE_WORKFLOW_EXECUTION_TYPE,
} from '@issuemanager/constants';
import {
  Divider,
  Typography,
  TypographyProps,
  useEventCallback,
} from '@mui/material';
import { Stack, styled } from '@mui/system';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { ActionFields } from '../../issue/manage-issue/action-fields';
import {
  ExtendedActionField,
  useActionsForm,
  useIssueTemplate,
  useIssueTemplateForm,
} from './hooks';

export const StyledTypographyNumber = styled(Typography)<
  WithTheme<TypographyProps>
>(({ theme }) => ({
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  minWidth: '24px',
  textAlign: 'center',
  marginRight: theme.spacing(2),
}));

const defaultValues = {
  title: '',
  issueTypeId: '',
  issueSubtypeId: undefined,
  isAutomatic: false,
  executionType: ISSUE_WORKFLOW_EXECUTION_TYPE.SEQUENTIAL,
  ownerId: null,
  approverId: null,
  severity: undefined,
  dueDate: null,
  govmapGroups: [],
  actions: [
    {
      title: '',
      dueDate: null,
      description: null,
      ownerId: null,
      customId: uuidv4(),
      // NOTE: related entities will be used in the future so far are not
      // needed. Ticket RCP-12663
      // govmapGroups: [],
    },
  ],
};

export default function () {
  const { slug } = useParams<{
    slug: string;
  }>();

  const [searchParams] = useSearchParams();
  const issueTypeId = searchParams.get('issueTypeId') || undefined;
  const issueSubtypeId = searchParams.get('issueSubtypeId') || undefined;

  const semantic = useAtlasSemanticThemeTokens();
  const { t } = useTranslation(['common', 'issue', 'settings']);
  const { setHeaderProps } = useHeaderStore();
  const navigate = useNavigate();

  const form = useIssueTemplateForm({
    issueTypeId,
    issueSubtypeId,
    defaultValues,
  });
  const actionsForm = useActionsForm({
    form: form.form,
    defaultValues,
  });

  const createIssueTemplate = useCreateIssueTemplate();
  const updateIssueTemplate = useUpdateIssueTemplate();

  const [executionType] = form.form.watch(['executionType']);

  const handleDragEnd = useEventCallback((actions: ExtendedActionField[]) => {
    form.form.setValue('actions', actions);
    if (form.form.formState.isSubmitted) {
      form.form.trigger('actions');
    } else {
      actionsForm.actions.forEach((action, index) => {
        if (action.dueDate) {
          form.form.trigger(`actions.${index}.dueDate`);
        }
      });
    }
  });
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const monitorAddIssueTemplatesFormSubmit =
    useMonitorAddIssueTemplatesFormSubmit();
  const monitorEditIssueTemplateFormSubmit =
    useMonitorEditIssueTemplateFormSubmit();
  const monitorEditIssueTemplateFormLoad =
    useMonitorEditIssueTemplateFormLoad();
  const submitIssueTemplateForm = useEventCallback(
    form.form.handleSubmit(async (e) => {
      closeSnackbar();

      if (!e.actions || e.actions.length === 0) {
        enqueueSnackbar(
          t('settings:issueTemplates.form.errorMessages.actions.atLeastOne'),
          { variant: 'error' },
        );
        return;
      }

      const issueTemplate = {
        ...e,
        severity: e.severity !== null ? e.severity : undefined,
        dueDate: e.dueDate ? parseInt(String(e.dueDate), 10) : null,
        actions: e.actions.map((action, index) => ({
          ...action,
          dueDate: action.dueDate ? parseInt(String(action.dueDate), 10) : null,
          sequence: index,
        })),
      };

      if (!slug) {
        createIssueTemplate.mutate(issueTemplate, {
          onSuccess: (createdIssueTemplate) => {
            if (createdIssueTemplate.slug) {
              navigate(
                `/${paths.settings.index}/${paths.issueTemplates.index}`,
              );
              enqueueSnackbar(
                t('settings:issueTemplates.form.create.success'),
                {
                  variant: 'success',
                },
              );
            }
          },
        });
      } else {
        updateIssueTemplate.mutate(
          {
            ...issueTemplate,
            slug,
          },
          {
            onSuccess: (updatedIssueTemplate) => {
              enqueueSnackbar(
                t('settings:issueTemplates.form.update.success'),
                {
                  variant: 'success',
                },
              );

              if (updatedIssueTemplate.slug !== slug) {
                navigate(
                  `/${paths.settings.index}/${paths.issueTemplates.index}`,
                );
              }
            },
          },
        );
      }
    }),
  );

  useEffect(() => {
    const addBtnText = createIssueTemplate.isPending
      ? t('settings:issueTemplates.form.formActions.adding')
      : t('settings:issueTemplates.form.formActions.add');

    const editBtnText = updateIssueTemplate.isPending
      ? t('settings:issueTemplates.form.formActions.editing')
      : t('settings:issueTemplates.form.formActions.edit');

    setHeaderProps({
      viewTitle: {
        breadcrumbSections: [
          { url: '/', title: t('common:appName') },
          {
            url: `/${paths.settings.index}/${paths.issueTemplates.index}`,
            title: t('common:breadcrumbs.issueTemplates'),
          },
        ],
        title: t('settings:routeName'),
      },
      buttons: [
        {
          variant: 'outlined',
          startIcon: <CloseIcon />,
          onClick: () =>
            navigate(`/${paths.settings.index}/${paths.issueTemplates.index}`),
          'data-testid': 'cancel-issue-template',
          children: t('settings:issueTemplates.form.formActions.cancel'),
        },
        {
          variant: 'contained',
          startIcon: <CheckIcon />,
          onClick: submitIssueTemplateForm,
          loading:
            createIssueTemplate.isPending || updateIssueTemplate.isPending,
          'data-testid': 'save-issue-template',
          children: slug ? editBtnText : addBtnText,
        },
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    submitIssueTemplateForm,
    createIssueTemplate.isPending,
    updateIssueTemplate.isPending,
    slug,
  ]);

  const issueTemplate = useIssueTemplate({
    slug,
    issueTypeId,
    issueSubtypeId,
  });
  useAllQueries({
    queries: [
      issueTemplate,
      form.issueTypesQuery,
      form.issueSubtypesQuery,
      form.slasQuery,
    ],
    onSuccess: () => {
      monitorEditIssueTemplateFormLoad.stop();
    },
    onError: () => {
      monitorEditIssueTemplateFormLoad.error();
    },
  });
  useEffect(() => {
    if (issueTemplate.isSuccess) {
      form.form.reset({
        ...issueTemplate.data,
        issueTypeId: issueTemplate.data.issueTypeId,
        issueSubtypeId: issueTemplate.data.issueSubtypeId ?? undefined,
        dueDate: issueTemplate.data.dueDate
          ? parseInt(String(issueTemplate.data.dueDate), 10)
          : undefined,
        govmapGroups: issueTemplate.data.govmapGroups.map((item) => item.id),
        severity: issueTemplate.data.severity,
        actions: issueTemplate.data.actions.map((action) => ({
          ...action,
          govmapGroups: action.govmapGroups.map((item) => item.id),
          dueDate: action.dueDate
            ? parseInt(String(action.dueDate), 10)
            : undefined,
          customId: action.id,
        })),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueTemplate.dataUpdatedAt]);

  useAllMutations({
    mutations: [createIssueTemplate],
    onStart: () => {
      monitorAddIssueTemplatesFormSubmit.start();
    },
    onSuccess: () => {
      monitorAddIssueTemplatesFormSubmit.stop();
    },
    onError: () => {
      monitorAddIssueTemplatesFormSubmit.error();
    },
  });
  useAllMutations({
    mutations: [updateIssueTemplate],
    onStart: () => {
      monitorEditIssueTemplateFormSubmit.start();
    },
    onSuccess: () => {
      monitorEditIssueTemplateFormSubmit.stop();
    },
    onError: () => {
      monitorEditIssueTemplateFormSubmit.error();
    },
  });
  return (
    <Stack mx={4} my={4} spacing={4} data-testid='add-issue-template'>
      {issueTemplate.isLoading ? (
        <LoadingProgress height={600} />
      ) : (
        <FormProvider {...form.form}>
          <Stack bgcolor='white' px={4} py={4} spacing={4}>
            <Stack direction='row' alignItems='end' spacing={3}>
              {form.fields.title}
            </Stack>
            <Stack spacing={2} data-testid='add-issue-container'>
              <Stack spacing={1}>
                <Typography variant='h2' fontWeight='medium'>
                  {t('settings:issueTemplates.form.sections.addIssue.title')}
                </Typography>
                <Typography>
                  {t('settings:issueTemplates.form.sections.addIssue.subtitle')}
                </Typography>
              </Stack>
              <Stack width='50%' spacing={2}>
                {form.fields.issueType}
                {form.fields.issueSubtype}
              </Stack>
            </Stack>

            <Stack spacing={2} data-testid='manage-issue-container'>
              <Stack spacing={1}>
                <Typography variant='h2' fontWeight='medium'>
                  {t('settings:issueTemplates.form.sections.manageIssue.title')}
                </Typography>
                <Typography>
                  {t(
                    'settings:issueTemplates.form.sections.manageIssue.subtitle',
                  )}
                </Typography>
              </Stack>
              <Stack direction='row' spacing={4}>
                <Stack spacing={3} flex={1}>
                  {form.fields.managerId}
                  {form.fields.ownerId}
                  <Stack spacing={2}>{form.fields.approverId}</Stack>
                </Stack>
                <Divider
                  orientation='vertical'
                  flexItem
                  style={{ color: semantic.color.background.container.value }}
                />
                <Stack flex={1} spacing={3}>
                  {form.fields.severity}
                  {form.fields.dueDate}
                  {form.fields.govmapGroups}
                </Stack>
              </Stack>
            </Stack>

            <ActionFields
              executionType={executionType}
              issueFields={form.fields}
              actionFields={actionsForm.actionFields}
              actionsFormValues={actionsForm.actions}
              actionValues={
                issueTemplate.data?.actions?.map((a) => ({
                  ...a,
                  status: ActionStatus.DRAFT,
                })) || []
              }
              onAddAction={actionsForm.handleAddAction}
              isEditing
              isEditable
              isWorkflowBlocked={false}
              onDragEnd={handleDragEnd}
            />
          </Stack>
        </FormProvider>
      )}
    </Stack>
  );
}
